import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import { ComponentProps, FC, useCallback, useMemo, useState } from 'react'
import { ResponsiveWaitingCampaignCell } from './ResponsiveWaitingCampaignCell'
import { SectionCardContainer } from '~/components/common/SectionCardContainer'
import {
  CampaignOrderField,
  CampaignStatus,
  useWaitingCampaignListQuery,
  useWaitingPublishCampaignsQuery,
} from '~/generated/graphql'
import { isRevisionRequired, isApprovalRequest } from '~/lib/CampaignStatus'
import { WaitingCampaignsTab, TabState, TabStateLabel } from './ResponsiveCampaignsTab'
import HelpIcon from '@mui/icons-material/HelpOutline'
import Link from 'next/link'
import { LINE_OFFICIAL_ACCOUNT_LINK } from '~/lib/config'
import { theme } from '~/lib/theme'
import { createCampaignMock } from '~/mocks/createCampaignMock'
import { encodeId } from '~/lib/convertId'

const WaitingCampaignLoading: FC = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Card elevation={0}>
      <CardContent sx={{ overflowX: 'scroll', scrollbarGutter: 'stable' }}>
        <Stack direction={isMobile ? 'column' : 'row'} gap={2}>
          {new Array(5).fill('').map((_, i) => (
            <Skeleton key={i} sx={{ flex: isMobile ? undefined : '0 0 185px' }}>
              <ResponsiveWaitingCampaignCell
                campaign={{
                  ...createCampaignMock({ name: 'dummy'.repeat(10) }),
                  id: encodeId('Campaign', i),
                  status: 'APPROVAL_REQUEST',
                  coverImageUrls: [],
                }}
              />
            </Skeleton>
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}

const CampaignCards = ({
  campaigns,
  loading,
  tabState,
}: {
  campaigns: ReadonlyArray<ComponentProps<typeof ResponsiveWaitingCampaignCell>['campaign']>
  loading: boolean
  tabState: TabState
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (loading) return <WaitingCampaignLoading />

  const hasCampaigns = campaigns.length
  if (!hasCampaigns) {
    return <Alert severity="info">{`${TabStateLabel[tabState]}のキャンペーンはありません。`}</Alert>
  }

  return (
    <>
      {tabState === TabState.RevisionRequired && (
        <>
          <Box sx={{ mt: 1, pl: 2 }}>
            <Button onClick={() => setIsDialogOpen(true)} sx={{ p: 0 }}>
              <Stack direction="row" justifyContent="flex-start" alignItems="center" gap={1}>
                <HelpIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography
                  variant="labelSJpNormal"
                  color="text.secondary"
                  sx={{
                    textDecoration: 'underline',
                  }}
                >
                  キャンペーンの修正に関するお問い合わせ
                </Typography>
              </Stack>
            </Button>
          </Box>
          <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="sm">
            <DialogContent sx={{ px: 3, pt: 3, pb: 0 }}>
              <Stack direction="column" gap={3}>
                <Typography variant="bodyM" color="text.primary">
                  修正に関する質問はtoridori marketing 公式LINEアカウントにて承っております。
                  <br />
                  登録がお済みでない方はtoridori marketing 公式LINEアカウントの友だち追加をお願いします。
                </Typography>
                <Typography variant="bodyM" color="text.secondary">
                  ※キャンペーンに関して運営とやり取りを行っている場合は、そちらをご確認下さい。
                  <br />
                  ※キャンペーンの申請を自分で取り下げた場合は、直接修正にお進みください
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', px: 0, pt: 2, pb: 3 }}>
              <Link href={LINE_OFFICIAL_ACCOUNT_LINK} target="_blank" rel="noopener noreferrer">
                <Button variant="contained" sx={{ backgroundColor: 'rgba(15, 200, 85, 1)' }}>
                  審査結果について質問する
                </Button>
              </Link>
            </DialogActions>
          </Dialog>
        </>
      )}
      <Card elevation={0}>
        <CardContent sx={{ overflowX: 'scroll', scrollbarGutter: 'stable', scrollbarWidth: 'none' }}>
          <Stack direction={isMobile ? 'column' : 'row'} gap={2}>
            {campaigns.map((x) => (
              <ResponsiveWaitingCampaignCell key={x.id} campaign={x} />
            ))}
            {/* 最終要素の右パディング用 */}
            <Box pr={1} />
          </Stack>
        </CardContent>
      </Card>
    </>
  )
}

const waitingCampaignInput = {
  input: {
    status: [CampaignStatus.ApprovalRequest, CampaignStatus.RevisionRequired],
    orderBy: {
      direction: 'DESC',
      field: CampaignOrderField.Id,
    },
  },
} as const
export const ResponsiveWaitingCampaignList: FC = () => {
  const { data, loading } = useWaitingCampaignListQuery({
    variables: waitingCampaignInput,
  })

  const { data: waitingPublishCampaigns, loading: waitingPublishCampaignsLoading } = useWaitingPublishCampaignsQuery()

  const revisionRequiredCampaigns = useMemo(
    () => (data?.viewer.campaigns ?? []).filter((campaign) => isRevisionRequired(campaign.status)),
    [data],
  )
  const waitingForReviewCampaigns = useMemo(
    () => (data?.viewer.campaigns ?? []).filter((campaign) => isApprovalRequest(campaign.status)),
    [data],
  )

  const [tabState, setTabState] = useState<TabState>(TabState.RevisionRequired)
  const handleTabChange = useCallback((tabValue: TabState) => setTabState(tabValue), [])

  return (
    <SectionCardContainer title="公開前キャンペーン" icon={<HourglassBottomIcon sx={{ fontSize: '20px' }} />}>
      <WaitingCampaignsTab tabState={tabState} handleTabChange={handleTabChange} loading={loading}>
        {tabState === TabState.RevisionRequired && (
          <CampaignCards campaigns={revisionRequiredCampaigns} loading={loading} tabState={TabState.RevisionRequired} />
        )}
        {tabState === TabState.WaitingForPublication && (
          <CampaignCards
            campaigns={waitingPublishCampaigns?.waitingPublishCampaigns ?? []}
            loading={waitingPublishCampaignsLoading}
            tabState={TabState.WaitingForPublication}
          />
        )}
        {tabState === TabState.WaitingForReview && (
          <CampaignCards campaigns={waitingForReviewCampaigns} loading={loading} tabState={TabState.WaitingForReview} />
        )}
      </WaitingCampaignsTab>
    </SectionCardContainer>
  )
}
